import { useParams } from 'react-router-dom';
import React, { ReactNode, useEffect, useState } from 'react';
import { Alert, Button, useTheme, Box, SxProps, Grid, CircularProgress } from '@mui/material';

import AppLoaderContent from '../../../app/features/loader/content/AppLoaderContent';
import CAppSubTitle from '../../../app/components/CAppSubTitle';
import useDocumentFiles from '../hooks/useDocumentFiles';
import _ from 'lodash';

import { useTranslation } from 'react-i18next';
import CAppPaper from '../../../app/components/CAppPaper';
import AppCustomIcon from '../../../app/features/icon/AppIcon';
import CChipFund from '../../investor/components/CChipFund';

import InvestorRoutes from '../../investor/InvestorRoutes';
import { useNavigate } from 'react-router-dom';
import AppFormatDate from '../../../app/features/format/AppFormatDate';

import { Helmet } from 'react-helmet';
import { useAuthContext } from '../../auth/contexts/AuthContext';
import DocumentRoutes from '../DocumentRoutes';
import useDocumentTypes from '../hooks/useDocumentTypes';
import useDocumentFile from '../hooks/useDocumentFile';
import AppError404 from '../../../app/features/error/404/AppError404';
import HAppRoutes from '../../../app/features/routes/helpers/HAppRoutes';
import langDocumentFile from '../langs/langDocumentFile';
import langDocumentType from '../langs/langDocumentType';
import useInvestExpertises from '../../investor/hooks/expertise/useInvestExpertises';
import CAppBreadcrumbs from '../../../app/components/CAppBreadcrumbs';
import langDocumentFiles from '../langs/langDocumentFiles';
import VDocumentFilesTable from '../views/VDocumentFilesTable';
import IDocumentFile from '../interfaces/IDocumentFile';
import FDocumentViewer from '../features/viewer/FDocumentViewer';
import IFundType from '../../investor/interfaces/expertise/IFundType';
import { langLookup } from '../../../app/utils/langLookup';

const isCompanyDocument = (document: IDocumentFile) => {
	return !document.fund && !!document.company;
};

const getBreadcrumbItems = (currentDocument: IDocumentFile, currentFundType?: IFundType) => {
	return isCompanyDocument(currentDocument)
		? [
				{
					label: currentDocument?.company?.name ?? 'unknown_name_company',
					location: HAppRoutes.getPath(DocumentRoutes.documents_company.tag),
				},
				{
					label: currentDocument?.name.replace(/_/g, ' '),
				},
		  ]
		: [
				{
					label: currentFundType?.name,
					location: HAppRoutes.getPath(DocumentRoutes.documents_expertise.tag, {
						investExpertiseTag: currentFundType?.tag,
					}),
				},
				{
					label: currentDocument.fund?.name ?? 'unknown_name_fund',
					location: HAppRoutes.getPath(DocumentRoutes.documents_fund.tag, {
						investExpertiseTag: currentFundType?.tag,
						investFundId: currentDocument.fund?.id.toString(),
					}),
				},

				{
					label: currentDocument?.name.replace(/_/g, ' '),
				},
		  ];
};

const PDocumentFile = () => {
	const routeParams = useParams();
	const documentId = routeParams.id ? Number.parseInt(routeParams.id) : null;

	const { document: currentDocument, download } = useDocumentFile(documentId);
	const { t } = useTranslation();
	const authContext = useAuthContext();
	const navigate = useNavigate();
	const investExpertises = useInvestExpertises();

	const { documents } = useDocumentFiles();
	const [documentsFiltered, setDocumentsFiltered] = useState<IDocumentFile[]>();

	const [state, setState] = useState<'start' | 'end'>();
	const [error, setError] = useState<'404' | 'no_current_document' | Error | null>(null);
	const handleDownload = () => {
		setState('start');
		download()
			.then(() => {
				setState('end');
			})
			.catch((e) => {
				setError(e);
			});
	};

	const { documentTypes } = useDocumentTypes();

	const [isViewable, setIsViewable] = useState<boolean>(false);
	useEffect(() => {
		if (currentDocument) {
			const tExt = currentDocument.name.split('.');
			if (['pdf'].includes(tExt[tExt.length - 1])) {
				setIsViewable(true);
			} else {
				setIsViewable(false);
			}
		}
	}, [currentDocument]);

	const [documentTypeName, setDocumentTypeName] = useState<string>();
	useEffect(() => {
		if (documentTypes && currentDocument && currentDocument.documentType) {
			const documentType = documentTypes.find((x) => x.tag === currentDocument.documentType);
			documentType && setDocumentTypeName(t(langLookup(langDocumentType, documentType.name)?.tag ?? ''));
		}
	}, [currentDocument, t, documentTypes]);

	const [currentFundType, setCurrentFundType] = useState<IFundType>();
	useEffect(() => {
		if (currentDocument && currentDocument.fund && investExpertises) {
			const mapFundType = investExpertises.find((x) => x.tag === currentDocument.fund?.typeTag);
			mapFundType && setCurrentFundType(mapFundType);
		}
	}, [currentDocument, investExpertises, t]);

	useEffect(() => {
		if (currentDocument && documents && investExpertises) {
			const documentsTmp = _.cloneDeep(documents.filter((x) => x.documentType === currentDocument.documentType));

			setDocumentsFiltered(
				_.orderBy(documentsTmp, ['isNew', 'fund.isRealized', 'date'], ['desc', 'asc', 'desc']),
			);
		}
	}, [currentDocument, documents, investExpertises]);

	return (
		<>
			{currentDocument === null ? (
				<AppError404 variant="container" />
			) : currentDocument && documentTypeName ? (
				<>
					<Helmet>
						<title>
							{authContext.authContext === 'employee' ? '[E] ' : ''} Document -{' '}
							{authContext.currentCompany ? authContext.currentCompany.name : ''} -{' '}
							{currentDocument.name.replace(/_/g, ' ')}
						</title>
					</Helmet>
					<CAppBreadcrumbs
						items={[
							{
								label: t(langDocumentFiles.title.tag),
								location: HAppRoutes.getPath(DocumentRoutes.dashboard.tag),
							},
							...getBreadcrumbItems(currentDocument, currentFundType),
						]}
					/>

					<Grid container spacing={3} sx={{ marginTop: '2rem', marginBottom: '30px' }}>
						{!!currentDocument.fund && (
							<Grid item sm={3} xs={12}>
								<BoxItem
									label={t(langDocumentFile.fund_name.tag)}
									value={
										<CChipFund
											onClick={() =>
												navigate(
													HAppRoutes.getPath(InvestorRoutes.fund.tag, {
														investExpertiseTag: currentDocument.fund?.typeTag ?? undefined,
														id: currentDocument.fund?.id.toString(),
													}),
												)
											}
											disabled={currentDocument.fund?.isRealized ?? undefined}
											label={currentDocument.fund?.name}
										/>
									}
								/>
							</Grid>
						)}

						{!!currentFundType && (
							<Grid item sm={2} xs={6}>
								<BoxItem label={t(langDocumentFile.fund_type.tag)} value={t(currentFundType.name)} />
							</Grid>
						)}

						<Grid item sm={2} xs={6}>
							<BoxItem
								label={t(langDocumentFile.released_date.tag)}
								value={<AppFormatDate date={currentDocument.date} />}
							/>
						</Grid>
						<Grid item sm={3} xs={12}>
							<BoxItem label={t(langDocumentFile.document_type.tag)} value={documentTypeName} />
						</Grid>
					</Grid>
					<Grid container spacing={3}>
						<Grid item sm={isViewable ? 4 : 12} xs={12}>
							<CAppPaper
								variant="elevation"
								color="primary"
								displayBackgroundImage
								sx={{ textAlign: 'center' }}
							>
								<Box>
									<AppCustomIcon
										tag={`document_format_${currentDocument.formatTag}`}
										sx={{ fontSize: '50px', marginBottom: '20px' }}
									/>
								</Box>
								<Box
									id="document-file-name"
									sx={{ fontSize: '1.5rem', lineHeight: '1.5', fontWeight: '600', width: '100%' }}
								>
									{currentDocument.name.replace(/_/g, ' ')}
								</Box>
								<Box sx={{ marginTop: '30px' }}>
									{error ? (
										<Alert severity="error">
											{error === '404' ? (
												<>{t(langDocumentFile.downloading_error_404.tag)}</>
											) : error === 'no_current_document' ? (
												<>{t(langDocumentFile.downloading_error_no_current_document.tag)}</>
											) : error instanceof Error ? (
												error.message
											) : (
												error
											)}
										</Alert>
									) : (
										<>
											{state === 'start' ? (
												<CircularProgress color="secondary" size={50} />
											) : (
												<Button
													id="btn-document-file-download"
													variant="contained"
													disabled={!currentDocument}
													color="secondary"
													sx={{ paddingLeft: '30px', paddingRight: '30px' }}
													onClick={() => handleDownload()}
												>
													{t(langDocumentFile.btn_download.tag)}
												</Button>
											)}
										</>
									)}
								</Box>
							</CAppPaper>
						</Grid>
						{isViewable && documentId !== null ? (
							<Grid item sm={8} xs={12}>
								<FDocumentViewer documentId={documentId} onError={() => setIsViewable(false)} />
							</Grid>
						) : (
							<></>
						)}
					</Grid>

					<CAppSubTitle
						title={t(langDocumentFile.subtitle_looking_other_x_documents.tag, { documentTypeName })}
					/>
					<VDocumentFilesTable
						documents={documentsFiltered || []}
						displayColumns={['name', 'fund.name', 'fundTypeName', 'date', 'id']}
					/>
				</>
			) : (
				<AppLoaderContent />
			)}
		</>
	);
};

type BoxItemProps = {
	label: string | ReactNode;
	value: string | number | ReactNode;
};
const BoxItem = (props: BoxItemProps) => {
	const theme = useTheme();
	const sxItem = {
		'.label': {
			fontWeight: 600,
			fontSize: '1rem',
			lineHeight: '2',
			color: theme.palette.mode === 'dark' ? '#FFF' : theme.palette.primary.main,
		},
		'.value': {
			fontWeight: 400,
			fontSize: '0.9rem',
			lineHeight: '1.5',
			color: theme.palette.mode === 'dark' ? '#FFF' : theme.palette.primary.main,
		},
	} as SxProps;
	return (
		<Box sx={sxItem}>
			<Box className="label">{props.label}</Box>
			<Box className="value">{props.value}</Box>
		</Box>
	);
};

export default PDocumentFile;
